// T025 — Proposal analytics event definitions.

import type { EventDefinition } from './navigation';

export type ProposalFormStartedProperties = {
  source: string;
};

export type ProposalFormStepCompletedProperties = {
  step: number;
  stepName: string;
};

export type ProposalCreatedProperties = {
  proposalId: string;
  category: string;
  numOutcomes: number;
  fundAmount: number;
};

export type ProposalCreateLoginRequestedProperties = {
  flow: 'manual' | 'generate';
  sourceStep: 4;
  resumeMechanism: 'in_place';
};

export type ProposalAuthContinuationRestoredProperties = {
  flow: 'manual' | 'generate';
  sourceStep: 4;
  outcome: 'restored';
  resumeMechanism: 'in_place' | 'session_remount';
  elapsedMs: number;
};

export type ProposalAuthContinuationFailedProperties = {
  flow: 'manual' | 'generate' | 'unknown';
  sourceStep: 4;
  outcome:
    | 'corrupt'
    | 'expired'
    | 'draft_missing'
    | 'draft_changed'
    | 'draft_incompatible'
    | 'storage_unavailable';
  resumeMechanism: 'in_place' | 'session_remount';
  elapsedMs: number;
};

export type ProposalAuthContinuationAbandonedProperties = {
  flow: 'manual' | 'generate';
  sourceStep: 4;
  outcome: 'created' | 'draft_cleared' | 'dialog_closed';
  resumeMechanism: 'in_place' | 'session_remount';
  elapsedMs: number;
};

export type ProposalBackedProperties = {
  traceId: string;
  proposalId: string;
  outcomeIndex: number;
  amount: number;
};

export type ProposalWithdrawnProperties = {
  traceId: string;
  proposalId: string;
  amount: number;
};

export type ProposalSearchProperties = {
  query: string;
};

export type ProposalFilterChangedProperties = {
  filterType:
    | 'category'
    | 'location'
    | 'status'
    | 'sort'
    | 'myProposals'
    | 'funding'
    | 'staked'
    | 'backers'
    | 'outcomes'
    | 'createdAt'
    | 'creator';
  value: string;
};

export type ProposalDetailViewedProperties = {
  proposalId: string;
};

export type ProposalWatchlistToggledProperties = {
  active: boolean;
};

export type ProposalAdminApprovedProperties = {
  proposalId: string;
};

export type ProposalAdminRejectedProperties = {
  proposalId: string;
  reason: string;
};

export type SuggestOutcomeModalOpenedProperties = {
  proposalPubkey: string;
};

export type CreatorRewardsPanelViewedProperties = {
  proposalId: string;
};

export const PROPOSAL_FORM_STARTED: EventDefinition<ProposalFormStartedProperties> = {
  name: 'proposal_form_started',
};

export const PROPOSAL_FORM_STEP_COMPLETED: EventDefinition<ProposalFormStepCompletedProperties> = {
  name: 'proposal_form_step_completed',
};

export const PROPOSAL_CREATED: EventDefinition<ProposalCreatedProperties> = {
  name: 'proposal_created',
};

export const PROPOSAL_CREATE_LOGIN_REQUESTED: EventDefinition<ProposalCreateLoginRequestedProperties> =
  { name: 'proposal_create_login_requested' };

export const PROPOSAL_AUTH_CONTINUATION_RESTORED: EventDefinition<ProposalAuthContinuationRestoredProperties> =
  { name: 'proposal_auth_continuation_restored' };

export const PROPOSAL_AUTH_CONTINUATION_FAILED: EventDefinition<ProposalAuthContinuationFailedProperties> =
  { name: 'proposal_auth_continuation_failed' };

export const PROPOSAL_AUTH_CONTINUATION_ABANDONED: EventDefinition<ProposalAuthContinuationAbandonedProperties> =
  { name: 'proposal_auth_continuation_abandoned' };

export const PROPOSAL_BACKED: EventDefinition<ProposalBackedProperties> = {
  name: 'proposal_backed',
};

export const PROPOSAL_WITHDRAWN: EventDefinition<ProposalWithdrawnProperties> = {
  name: 'proposal_withdrawn',
};

export const PROPOSAL_SEARCH: EventDefinition<ProposalSearchProperties> = {
  name: 'proposal_search',
};

export const PROPOSAL_FILTER_CHANGED: EventDefinition<ProposalFilterChangedProperties> = {
  name: 'proposal_filter_changed',
};

export const PROPOSAL_DETAIL_VIEWED: EventDefinition<ProposalDetailViewedProperties> = {
  name: 'proposal_detail_viewed',
};

export const PROPOSAL_WATCHLIST_TOGGLED: EventDefinition<ProposalWatchlistToggledProperties> = {
  name: 'proposal_watchlist_toggled',
};

export const PROPOSAL_ADMIN_APPROVED: EventDefinition<ProposalAdminApprovedProperties> = {
  name: 'proposal_admin_approved',
};

export const PROPOSAL_ADMIN_REJECTED: EventDefinition<ProposalAdminRejectedProperties> = {
  name: 'proposal_admin_rejected',
};

export const SUGGEST_OUTCOME_MODAL_OPENED: EventDefinition<SuggestOutcomeModalOpenedProperties> = {
  name: 'suggest_outcome_modal_opened',
};

export const CREATOR_REWARDS_PANEL_VIEWED: EventDefinition<CreatorRewardsPanelViewedProperties> = {
  name: 'creator_rewards_panel_viewed',
};

export type DuplicateNudgeShownProperties = {
  matchCount: number;
  topSimilarity: number;
};

export type UniqueMarketAffirmationShownProperties = {
  queryLength: number;
};

export type DuplicateNudgeDismissedProperties = {
  matchCount: number;
};

export const DUPLICATE_NUDGE_SHOWN: EventDefinition<DuplicateNudgeShownProperties> = {
  name: 'duplicate_nudge_shown',
};

export const UNIQUE_MARKET_AFFIRMATION_SHOWN: EventDefinition<UniqueMarketAffirmationShownProperties> =
  {
    name: 'unique_market_affirmation_shown',
  };

export const DUPLICATE_NUDGE_DISMISSED: EventDefinition<DuplicateNudgeDismissedProperties> = {
  name: 'duplicate_nudge_dismissed',
};
